import { CloudPlatform, normalizePlatform } from '@/pages/admin/settings/cloud_provider/CloudProviderCategory';
import { CloudProviderT } from '@/pages/admin/settings/cloud_provider/types';

export const awsRegionNameMap: Record<string, string> = {
  'us-east-1': 'US East (N. Virginia)',
  'us-east-2': 'US East (Ohio)',
  'us-west-1': 'US West (N. California)',
  'us-west-2': 'US West (Oregon)',
  'eu-central-1': 'Europe (Frankfurt)',
  'eu-west-1': 'Europe (Ireland)',
  'eu-west-2': 'Europe (London)',
  'eu-south-1': 'Europe (Milan)',
  'eu-west-3': 'Europe (Paris)',
  'eu-south-2': 'Europe (Spain)',
  'eu-north-1': 'Europe (Stockholm)',
  'eu-central-2': 'Europe (Zurich)',
  'sa-east-1': 'South America (São Paulo)',
  'ca-central-1': 'Canada (Central)',
  'ca-west-1': 'Canada West (Calgary)',
  'ap-east-1': 'Asia Pacific (Hong Kong)',
  'ap-south-2': 'Asia Pacific (Hyderabad)',
  'ap-southeast-3': 'Asia Pacific (Jakarta)',
  'ap-southeast-4': 'Asia Pacific (Melbourne)',
  'ap-south-1': 'Asia Pacific (Mumbai)',
  'ap-northeast-3': 'Asia Pacific (Osaka)',
  'ap-northeast-2': 'Asia Pacific (Seoul)',
  'ap-southeast-1': 'Asia Pacific (Singapore)',
  'ap-southeast-2': 'Asia Pacific (Sydney)',
  'ap-northeast-1': 'Asia Pacific (Tokyo)',
  'il-central-1': 'Israel (Tel Aviv)',
  'me-south-1': 'Middle East (Bahrain)',
  'me-central-1': 'Middle East (UAE)',
  'us-gov-east-1': 'AWS GovCloud (US-East)',
  'us-gov-west-1': 'AWS GovCloud (US-West)',
  'af-south-1': 'Africa (Cape Town)',
};

// Google Cloud region descriptions, matching the names shown in the Google Cloud console.
// https://cloud.google.com/artifact-registry/docs/repositories/repo-locations
export const gcpRegionNameMap: Record<string, string> = {
  'africa-south1': 'Johannesburg',
  'asia-east1': 'Taiwan',
  'asia-east2': 'Hong Kong',
  'asia-northeast1': 'Tokyo',
  'asia-northeast2': 'Osaka',
  'asia-northeast3': 'Seoul',
  'asia-south1': 'Mumbai',
  'asia-south2': 'Delhi',
  'asia-southeast1': 'Singapore',
  'asia-southeast2': 'Jakarta',
  'asia-southeast3': 'Bangkok',
  'australia-southeast1': 'Sydney',
  'australia-southeast2': 'Melbourne',
  'europe-central2': 'Warsaw',
  'europe-north1': 'Finland',
  'europe-north2': 'Stockholm',
  'europe-southwest1': 'Madrid',
  'europe-west1': 'Belgium',
  'europe-west2': 'London',
  'europe-west3': 'Frankfurt',
  'europe-west4': 'Netherlands',
  'europe-west6': 'Zürich',
  'europe-west8': 'Milan',
  'europe-west9': 'Paris',
  'europe-west10': 'Berlin',
  'europe-west12': 'Turin',
  'me-central1': 'Doha',
  'me-central2': 'Dammam',
  'me-west1': 'Tel Aviv',
  'northamerica-northeast1': 'Montréal',
  'northamerica-northeast2': 'Toronto',
  'northamerica-south1': 'Queretaro',
  'southamerica-east1': 'São Paulo',
  'southamerica-west1': 'Santiago',
  'us-central1': 'Iowa',
  'us-east1': 'South Carolina',
  'us-east4': 'Northern Virginia',
  'us-east5': 'Columbus',
  'us-south1': 'Dallas',
  'us-west1': 'Oregon',
  'us-west2': 'Los Angeles',
  'us-west3': 'Salt Lake City',
  'us-west4': 'Las Vegas',
};

const regionNameMaps: Record<string, string>[] = [awsRegionNameMap, gcpRegionNameMap];

function lookupRegionName(region: string) {
  return regionNameMaps.find((regionNames) => regionNames[region])?.[region];
}

// Formats a region as "<friendly name> · <region code>", e.g. "US East (Ohio) · us-east-2".
// Falls back to the bare region code when no friendly name is known.
export function formatRegionLabel(region: string, description?: string) {
  const friendlyName = lookupRegionName(region) || description;
  if (!friendlyName || friendlyName === region) {
    return region;
  }
  return `${friendlyName} · ${region}`;
}

export function getRegionName(cp: Pick<CloudProviderT, 'platform' | 'region'> | null) {
  if (!cp) {
    return '';
  }

  const platform = normalizePlatform(cp.platform);
  if (platform === CloudPlatform.AWS || platform === CloudPlatform.GCP) {
    return formatRegionLabel(cp.region);
  }

  return cp.region;
}
